import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  format,
  isSameDay,
  isWithinInterval,
  parseISO,
  isBefore,
  setMonth,
  setYear,
  getMonth,
} from "date-fns";
import { RiBox3Line } from "react-icons/ri";
import { BsBoxSeam } from "react-icons/bs";
import { IoTrendingDown, IoAnalyticsOutline } from "react-icons/io5";
import { IoIosReturnLeft } from "react-icons/io";
import { TbNotes } from "react-icons/tb";
import {
 TotalProductsIcon,
 TotalBinsIcon,
 InwardIcon,
 OutwardIcon,
 PutawayIcon,
 ReturnsIcon,
 NearExpiryIcon,
 ExpiredIcon
} from "../assets/states/StatsIcons";

import { dashboardService } from "../services/api";

// StatCard Component
const StatCard = ({ label, value, suffix, icon, iconBgColor, iconTextColor }) => {
  return (
    <div className="rounded-2xl border border-gray-200/60 bg-white p-5 shadow-sm h-[100px] w-full min-w-0 max-w-full">
      <div className="flex items-center gap-5 h-full">
        {/* Icon Container */}
        <div
  className={`h-[50px] w-[50px] grid place-items-center flex-shrink-0 ${iconBgColor} ${iconTextColor}`}
  style={{
    borderTopLeftRadius: "24px",
    borderTopRightRadius: "8px",
    borderBottomRightRadius: "24px",
    borderBottomLeftRadius: "8px",
  }}
>
  {icon}
</div>

        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-600 whitespace-nowrap truncate">{label}</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-lg font-semibold text-gray-900">{value}</span>
            {suffix && (
              <span className="text-xs font-medium text-gray-500 whitespace-nowrap">{suffix}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
const AlertIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
    />
  </svg>
);

const RefreshIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
);

// Build stats cards from API data (hoisted for FALLBACK_STATS)
const buildStatsFromApi = (stats) => {
  if (!stats) return [];
  return [
    { label: "Total Products", value: String(stats.totalProducts ?? 0), suffix: null, icon: <TotalProductsIcon size={20} color="#fff" />, iconBgColor: "bg-[#01C36F]", iconTextColor: "text-white" },
    { label: "Total Bins", value: String(stats.totalBins ?? 0), suffix: null, icon: <TotalBinsIcon size={20} color="#fff" />, iconBgColor: "bg-[#5B6EDC]", iconTextColor: "text-white" },
    { label: "Inward Transactions", value: String(stats.inwardLastMonth ?? 0), suffix: "(Last Month)", icon: <InwardIcon size={20} />, iconBgColor: "bg-[#E22879]", iconTextColor: "text-white" },
    { label: "Outward Transactions", value: String(stats.outwardLastMonth ?? 0), suffix: "(Last Month)", icon: <OutwardIcon size={20} />, iconBgColor: "bg-[#2CAEFF]", iconTextColor: "text-white" },
    { label: "Putaways", value: String(stats.putawayLastMonth ?? 0), suffix: "(Last Month)", icon: <PutawayIcon size={20} color="#fff" />, iconBgColor: "bg-[#89C313]", iconTextColor: "text-white" },
    { label: "Active Returns", value: String(stats.activeReturns ?? 0), suffix: null, icon: <ReturnsIcon size={20} />, iconBgColor: "bg-[#E83B3B]", iconTextColor: "text-white" },
    { label: "Product Near Expiry", value: String(stats.nearExpiry ?? 0), suffix: null, icon: <NearExpiryIcon size={20} />, iconBgColor: "bg-[#EE7C19]", iconTextColor: "text-white" },
    { label: "Product Expired", value: String(stats.expired ?? 0), suffix: null, icon: <ExpiredIcon size={20} />, iconBgColor: "bg-[#6359E9]", iconTextColor: "text-white" },
  ];
};

const FALLBACK_STATS = buildStatsFromApi({
  totalProducts: 0,
  totalBins: 0,
  inwardLastMonth: 0,
  outwardLastMonth: 0,
  putawayLastMonth: 0,
  activeReturns: 0,
  nearExpiry: 0,
  expired: 0,
});

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

/**
 * Returns a flat grid of 6*7 = 42 cells for the month: only this month's dates
 * appear; leading/trailing cells are null so the 1st aligns under the correct weekday (Mon–Sun).
 */
function getDaysForMonth(monthDate) {
  const first = startOfMonth(monthDate);
  const last = endOfMonth(monthDate);
  const daysInMonth = last.getDate();
  // Monday = column 0. getDay(): 0=Sun, 1=Mon, ... so leading empty slots:
  const firstWeekday = first.getDay(); // 0=Sun, 1=Mon, ...
  const leading = firstWeekday === 0 ? 6 : firstWeekday - 1; // Mon=0, Tue=1, ..., Sun=6
  const total = 42; // 6 rows * 7
  const trailing = total - leading - daysInMonth;
  const grid = [];
  for (let i = 0; i < leading; i++) grid.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    grid.push(new Date(first.getFullYear(), first.getMonth(), day));
  }
  for (let i = 0; i < trailing; i++) grid.push(null);
  return grid;
}

export const DashboardDateRangePopup = ({
  dateFrom,
  dateTo,
  displayMonth,
  setDisplayMonth,
  onSelectRange,
  onApply,
  onClear,
  onClose,
}) => {
  const fromDate = dateFrom ? parseISO(dateFrom) : null;
  const toDate = dateTo ? parseISO(dateTo) : null;
  const leftMonth = startOfMonth(displayMonth);
  const rightMonth = addMonths(leftMonth, 1);

  const handleDayClick = (d) => {
    const dateStr = format(d, "yyyy-MM-dd");
    if (!fromDate || (fromDate && toDate)) {
      onSelectRange(dateStr, "");
    } else {
      if (isBefore(d, fromDate)) {
        onSelectRange(dateStr, format(fromDate, "yyyy-MM-dd"));
      } else {
        onSelectRange(format(fromDate, "yyyy-MM-dd"), dateStr);
      }
    }
  };

  const leftDays = useMemo(() => getDaysForMonth(leftMonth), [leftMonth.getTime()]);
  const rightDays = useMemo(() => getDaysForMonth(rightMonth), [rightMonth.getTime()]);

  const renderDay = (d, monthRef, cellIndex) => {
    if (d == null) {
      return (
        <div
          key={`empty-${monthRef.getTime()}-${cellIndex}`}
          className="w-full aspect-square"
          style={{ maxWidth: 40 }}
          aria-hidden
        />
      );
    }
    const isStart = fromDate && isSameDay(d, fromDate);
    const isEnd = toDate && isSameDay(d, toDate);
    const isInRange =
      fromDate &&
      toDate &&
      !isStart &&
      !isEnd &&
      isWithinInterval(d, { start: fromDate, end: toDate });
    return (
      <button
        key={d.getTime()}
        type="button"
        onClick={() => handleDayClick(d)}
        className={`
          w-full aspect-square flex items-center justify-center text-[11px] font-medium rounded-[10px] border border-[#E5E7EB] bg-white text-gray-900
          focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:ring-offset-1
          ${isStart || isEnd ? "!bg-emerald-600 !border-emerald-600 !text-white" : ""}
          ${isInRange ? "!bg-emerald-50 !border-emerald-100 !text-gray-900" : ""}
          ${!isStart && !isEnd && !isInRange ? "hover:bg-gray-50" : ""}
        `}
        style={{ maxWidth: 40 }}
      >
        {format(d, "d")}
      </button>
    );
  };

  const monthYears = useMemo(() => {
    const y = displayMonth.getFullYear();
    return Array.from({ length: 11 }, (_, i) => y - 5 + i);
  }, [displayMonth]);

  return (
    <div
      className="fixed z-[9999] mt-3 bg-white rounded-[24px] border border-gray-200"
      style={{
        right: 24,
        padding: 16,
        boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
        maxWidth: 520,
        width: "min(100vw - 32px, 520px)",
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Two months side-by-side; stack on small screens. Month+Year centered above each grid */}
      <div className="flex flex-col md:flex-row gap-4 md:gap-2.5">
        {/* Left month */}
        <div className="flex flex-col flex-1 w-full overflow-hidden">
          <div className="flex items-center justify-between gap-2 mb-2">
            <button
              type="button"
              onClick={() => setDisplayMonth(subMonths(displayMonth, 1))}
              className="w-7 h-7 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-label="Previous months"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50">
              <select
                value={getMonth(leftMonth)}
                onChange={(e) => setDisplayMonth(setMonth(new Date(leftMonth), Number(e.target.value)))}
                className="text-xs font-medium text-gray-900 bg-transparent border-none cursor-pointer pr-4 appearance-none focus:outline-none focus:ring-0"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 0 center" }}
              >
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i}>{name}</option>
                ))}
              </select>
              <select
                value={leftMonth.getFullYear()}
                onChange={(e) => setDisplayMonth(setYear(new Date(leftMonth), Number(e.target.value)))}
                className="text-xs font-medium text-gray-900 bg-transparent border-none cursor-pointer pr-4 appearance-none focus:outline-none focus:ring-0"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 0 center" }}
              >
                {monthYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-2 mb-1.5">
            {WEEKDAYS.map((wd) => (
              <div key={wd} className="w-10 h-6 flex items-center justify-center text-[10px] font-semibold text-gray-800">
                {wd}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2 justify-items-center">
            {leftDays.map((d, i) => renderDay(d, leftMonth, i))}
          </div>
        </div>
        <div className="hidden md:block w-px bg-gray-200 self-stretch min-h-[200px]" aria-hidden />
        {/* Right month */}
        <div className="flex flex-col flex-1 w-full overflow-hidden">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50">
              <select
                value={getMonth(rightMonth)}
                onChange={(e) => setDisplayMonth(subMonths(new Date(rightMonth.getFullYear(), Number(e.target.value), 1), 1))}
                className="text-xs font-medium text-gray-900 bg-transparent border-none cursor-pointer pr-4 appearance-none focus:outline-none focus:ring-0"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 0 center" }}
              >
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i}>{name}</option>
                ))}
              </select>
              <select
                value={rightMonth.getFullYear()}
                onChange={(e) => setDisplayMonth(subMonths(new Date(Number(e.target.value), getMonth(rightMonth), 1), 1))}
                className="text-xs font-medium text-gray-900 bg-transparent border-none cursor-pointer pr-4 appearance-none focus:outline-none focus:ring-0"
                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 0 center" }}
              >
                {monthYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setDisplayMonth(addMonths(displayMonth, 1))}
              className="w-7 h-7 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-label="Next months"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </div>
          <div className="grid grid-cols-7 gap-2 mb-1.5">
            {WEEKDAYS.map((wd) => (
              <div key={`r-${wd}`} className="w-10 h-6 flex items-center justify-center text-[10px] font-semibold text-gray-800">
                {wd}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2 justify-items-center">
            {rightDays.map((d, i) => renderDay(d, rightMonth, i))}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-3 mt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onApply}
          className="rounded-xl border border-emerald-500/60 bg-emerald-50 px-3.5 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={onClear}
          className="rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
        >
          Close
        </button>
      </div>
      <div className="mt-3 h-[3px] w-full rounded-b-[24px] bg-emerald-500/80" />
    </div>
  );
};

const DashboardDemo = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState(FALLBACK_STATS);
  const [inwardOutwardData, setInwardOutwardData] = useState(null);
  const [batchDistributionData, setBatchDistributionData] = useState(null);
  const [binUtilizationData, setBinUtilizationData] = useState(null);
  const [returnsTrendData, setReturnsTrendData] = useState(null);
  const [hoveredReturnDay, setHoveredReturnDay] = useState(null);
  const [hoveredInwardWeek, setHoveredInwardWeek] = useState(null);
  const [hoveredBinSegment, setHoveredBinSegment] = useState(null);
  const [hoveredBatchBar, setHoveredBatchBar] = useState(null);
  // Date range: form values (single control = From + To)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Applied range sent to API; Refresh resets this + form to no filter
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");
  const [isDateOpen, setIsDateOpen] = useState(false);
  const dateRef = useRef(null);
  // First day of left month in the 2-month calendar view
  const [displayMonth, setDisplayMonth] = useState(() => new Date());

  const rangeLabel = useMemo(() => {
    const fmt = (v) => {
      if (!v) return "";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return v;
      const day = d.getDate();
      const mon = d.toLocaleString("en-GB", { month: "short" });
      return `${day} ${mon}`;
    };
    const a = fmt(dateFrom);
    const b = fmt(dateTo);
    if (!a && !b) return "Select Date";
    if (a && !b) return `${a} - ...`;
    if (!a && b) return `... - ${b}`;
    return `${a} - ${b}`;
  }, [dateFrom, dateTo]);

  // Fetch all dashboard data from API (with optional date range: from/to)
  const loadDashboardData = async (overrideParams) => {
    try {
      setLoading(true);
      const from = overrideParams && "from" in overrideParams ? overrideParams.from : appliedFrom;
      const to = overrideParams && "to" in overrideParams ? overrideParams.to : appliedTo;
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const response = await dashboardService.getDashboardOverview(Object.keys(params).length ? params : undefined);
      
      if (!response.success || !response.data) {
        throw new Error(response.message || "Failed to load dashboard");
      }

      const { stats, inwardOutwardTrend, batchDistribution, binUtilization, returnsTrend } = response.data;

      const isEmptyRange = !from && !to;

      let finalStats = stats;

      if (isEmptyRange && stats) {
        const lastMonth = {
          from: format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd"),
          to: format(endOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd"),
        };
        try {
          const lastMonthRes = await dashboardService.getDashboardOverview(lastMonth);
          if (lastMonthRes.success && lastMonthRes.data?.stats) {
            const lm = lastMonthRes.data.stats;
            finalStats = {
              ...stats,
              inwardLastMonth: lm.inwardLastMonth ?? 0,
              outwardLastMonth: lm.outwardLastMonth ?? 0,
              putawayLastMonth: lm.putawayLastMonth ?? 0,
            };
          } else {
            finalStats = {
              ...stats,
              inwardLastMonth: stats.inwardLastMonth ?? 0,
              outwardLastMonth: stats.outwardLastMonth ?? 0,
              putawayLastMonth: stats.putawayLastMonth ?? 0,
            };
          }
        } catch {
          finalStats = {
            ...stats,
            inwardLastMonth: stats.inwardLastMonth ?? 0,
            outwardLastMonth: stats.outwardLastMonth ?? 0,
            putawayLastMonth: stats.putawayLastMonth ?? 0,
          };
        }
      }

      setStatsData(buildStatsFromApi(finalStats));
      setInwardOutwardData(inwardOutwardTrend || { weeks: [], inward: [], outward: [] });
      setBatchDistributionData(batchDistribution || null);
      setBinUtilizationData(binUtilization || null);
      setReturnsTrendData(returnsTrend || { days: [], values: [] });
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      setStatsData(FALLBACK_STATS);
      setInwardOutwardData({ weeks: [], inward: [], outward: [] });
      setBatchDistributionData(null);
      setBinUtilizationData(null);
      setReturnsTrendData({ days: [], values: [] });
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when applied date range changes (Apply sends appliedFrom/To)
  useEffect(() => {
    loadDashboardData();
  }, [appliedFrom, appliedTo]);
  useEffect(() => {
    const onClick = (e) => {
      if (dateRef.current?.contains(e.target)) return;
      setIsDateOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
 
  // Apply date filter: set applied range and fetch immediately
  const handleApplyDateFilter = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    // loadDashboardData({ from: dateFrom, to: dateTo });
  };

  // Refresh: clear date range (back to no filter) and refetch
  const handleRefresh = () => {
    setDateFrom("");
    setDateTo("");
    setAppliedFrom("");
    setAppliedTo("");
    // loadDashboardData({ from: "", to: "" });
  };

  // Show loading state on initial load
  if (loading && statsData.length === 0) {
    return (
      <div className="pl-3 pr-6 py-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
   <div className="pl-3 pr-6 pt-2 pb-6 bg-gray-50 min-h-screen">
      {/* Header */}
      {/* Header */}
<div className="mb-6 w-full">
  {/* Capsule like screenshot */}
  <div
    className="rounded-[28px] border border-[#EEF1F5] bg-[#FFFFFF] px-6 py-4 shadow-sm"
    style={{ boxShadow: "0px 0px 4px 0px #959DA526" }}
  >
    <div className="flex items-center justify-between w-full flex-wrap gap-4">
      <h1
        style={{
          fontFamily: "Nunito",
          fontSize: 18,
          fontWeight: 600,
          color: "#161616",
          margin: 0,
        }}
      >
        Dashboard
      </h1>

      {/* Screenshot-like filter */}
      <div className="relative" ref={dateRef}>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Filter by Date :
          </span>

          <button
            type="button"
            onClick={() => {
              const open = !isDateOpen;
              if (open) {
                setDisplayMonth(startOfMonth(dateFrom ? parseISO(dateFrom) : new Date()));
              }
              setIsDateOpen(open);
            }}
            className="inline-flex items-center rounded-xl border border-emerald-500/60 bg-white px-5 py-2 text-sm font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50 active:scale-[0.99] transition"
            style={{ minWidth: 180, justifyContent: "center" }}
          >
            {rangeLabel}
          </button>
        </div>

        {isDateOpen && (
          <DashboardDateRangePopup
            dateFrom={dateFrom}
            dateTo={dateTo}
            displayMonth={displayMonth}
            setDisplayMonth={setDisplayMonth}
            onSelectRange={(from, to) => {
              setDateFrom(from);
              setDateTo(to);
            }}
            onApply={() => {
              handleApplyDateFilter();
              setIsDateOpen(false);
            }}
            onClear={() => {
              setDateFrom("");
              setDateTo("");
              setAppliedFrom("");
              setAppliedTo("");
            }}
            onClose={() => setIsDateOpen(false)}
          />
        )}
      </div>

      {/* ✅ refresh removed already */}
    </div>
  </div>
</div>


      {/* Stats Grid */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
        {statsData.map((stat, index) => (
          <StatCard
            key={index}
            label={stat.label}
            value={stat.value}
            suffix={stat.suffix}
            icon={stat.icon}
            iconBgColor={stat.iconBgColor}
            iconTextColor={stat.iconTextColor}
          />
        ))}
      </div>

      {/* Chart Cards Section */}
      <section className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2 w-full min-w-0">
        {/* Left Card: Inward vs Outward Trend */}
        <div className="rounded-3xl border border-gray-200/60 bg-white p-3 md:p-4 shadow-sm overflow-x-auto">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Inward vs Outward Trend</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 rounded-md bg-emerald-300"></div>
                <span className="text-xs text-gray-500">Inward</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 rounded-md bg-sky-400"></div>
                <span className="text-xs text-gray-500">Outward</span>
              </div>
            </div>
          </div>

          {/* Chart Area */}
          <div className="mt-6 relative" style={{ height: '320px' }}>
            {(() => {
              const inward = inwardOutwardData?.inward ?? [0, 0, 0, 0];
              const outward = inwardOutwardData?.outward ?? [0, 0, 0, 0];
              const weeks = inwardOutwardData?.weeks ?? ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
              const maxVal = Math.max(1, ...inward, ...outward);
              const xPos = [100, 250, 400, 550];
              const toY = (v) => 350 - (v / maxVal) * 300;
              
              const inwardPath = inward.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xPos[i]} ${toY(v)}`).join(' ');
              const outwardPath = outward.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xPos[i]} ${toY(v)}`).join(' ');
              const fillPath = inwardPath + ` L 550 350 L 100 350 Z`;
              
              return (
                <svg width="100%" height="100%" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid meet" className="overflow-visible">
                  <defs>
                    <linearGradient id="greenFill" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0.05" />
                    </linearGradient>
                  </defs>
                  
                  <line x1="50" y1="50" x2="550" y2="50" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="50" y1="120" x2="550" y2="120" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="50" y1="190" x2="550" y2="190" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="50" y1="260" x2="550" y2="260" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="50" y1="330" x2="550" y2="330" stroke="#e5e7eb" strokeWidth="1" />
                  <text x="30" y="55" fill="#6b7280" fontSize="12" textAnchor="end">{maxVal}</text>
                  <text x="30" y="365" fill="#6b7280" fontSize="12" textAnchor="end">0</text>
                  <path d={fillPath} fill="url(#greenFill)" stroke="none" />
                  <path d={inwardPath} fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  <path d={outwardPath} fill="none" stroke="#0ea5e9" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  
                  {inward.map((v, i) => (
                    <g key={`inward-${i}`}>
                      <circle 
                        cx={xPos[i]} 
                        cy={toY(v)} 
                        r="15" 
                        fill="transparent"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setHoveredInwardWeek({ index: i, type: 'inward', value: v })}
                        onMouseLeave={() => setHoveredInwardWeek(null)}
                      />
                      <circle 
                        cx={xPos[i]} 
                        cy={toY(v)} 
                        r="6" 
                        fill="#10b981" 
                        stroke="white" 
                        strokeWidth="2"
                        pointerEvents="none"
                      />
                    </g>
                  ))}
                  {outward.map((v, i) => (
                    <g key={`outward-${i}`}>
                      <circle 
                        cx={xPos[i]} 
                        cy={toY(v)} 
                        r="15" 
                        fill="transparent"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setHoveredInwardWeek({ index: i, type: 'outward', value: v })}
                        onMouseLeave={() => setHoveredInwardWeek(null)}
                      />
                      <circle 
                        cx={xPos[i]} 
                        cy={toY(v)} 
                        r="6" 
                        fill="#0ea5e9" 
                        stroke="white" 
                        strokeWidth="2"
                        pointerEvents="none"
                      />
                    </g>
                  ))}
                  
                  {hoveredInwardWeek !== null && (
                    <g transform={`translate(${xPos[hoveredInwardWeek.index]}, ${Math.max(60, toY(hoveredInwardWeek.value) - 45)})`} pointerEvents="none">
                      <rect x="-60" y="-28" width="120" height="36" rx="8" fill="white" stroke="#e5e7eb" strokeWidth="1.5" filter="drop-shadow(0 2px 8px rgba(0,0,0,0.15))" />
                      <text x="0" y="-10" fill="#111827" fontSize="14" textAnchor="middle" fontWeight="600">{hoveredInwardWeek.value}</text>
                      <text x="0" y="6" fill="#6b7280" fontSize="11" textAnchor="middle">{hoveredInwardWeek.type === 'inward' ? 'Inward' : 'Outward'}</text>
                    </g>
                  )}
                  
                  {weeks.map((w, i) => (
                    <text key={i} x={xPos[i]} y="385" fill="#6b7280" fontSize="12" textAnchor="middle">{w}</text>
                  ))}
                </svg>
              );
            })()}
          </div>
        </div>

        {/* Right Card: Batch Distribution by Status */}
        <div className="rounded-3xl border border-gray-200/60 bg-white p-3 md:p-4 shadow-sm overflow-visible">
          <h3 className="text-base font-semibold text-gray-900">Batch Distribution by Status</h3>

          {/* Bar Chart Area - no overflow so tooltip is not clipped */}
          <div className="mt-10 flex justify-center gap-4 md:gap-8 items-end">
            {[
              { key: 'good', label: 'Good', data: batchDistributionData?.good, gradient: 'from-emerald-300 to-emerald-500' },
              { key: 'allocated', label: 'Allocated', data: batchDistributionData?.allocated, gradient: 'from-sky-300 to-sky-500' },
              { key: 'onHold', label: 'On Hold', data: batchDistributionData?.onHold, gradient: 'from-orange-300 to-orange-500' },
              { key: 'nearExpiry', label: 'Near Expiry', data: batchDistributionData?.nearExpiry, gradient: 'from-yellow-300 to-yellow-500' },
              { key: 'expired', label: 'Expired', data: batchDistributionData?.expired, gradient: 'from-red-300 to-red-500' },
              { key: 'damaged', label: 'Damaged', data: batchDistributionData?.damaged, gradient: 'from-violet-300 to-violet-500' },
            ].map(({ key, label, data, gradient }) => {
              const pct = data?.percentage ?? 0;
              const val = data?.value ?? 0;
              const heightPct = Math.min(100, Math.max(5, pct));
              const isHovered = hoveredBatchBar === key;
              return (
                <div
                  key={key}
                  className="flex flex-col items-center relative"
                  style={{ cursor: 'pointer', zIndex: isHovered ? 20 : 0 }}
                  onMouseEnter={() => setHoveredBatchBar(key)}
                  onMouseLeave={() => setHoveredBatchBar(null)}
                >
                  <div className="relative flex flex-col items-center">
                    <div className="w-[48px] h-[285px] rounded-2xl bg-gray-100/60 flex items-end relative">
                      <div className={`w-[48px] rounded-2xl bg-gradient-to-b ${gradient} relative`} style={{ height: `${heightPct}%` }}>
                        {isHovered && (
                          <div className="absolute -top-14 left-1/2 transform -translate-x-1/2 bg-white rounded-lg px-4 py-2 shadow-md border border-gray-200 whitespace-nowrap pointer-events-none z-[100]" style={{ minWidth: '80px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                            <div className="text-sm font-semibold text-gray-900">{val}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{label}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <span className="mt-4 text-center text-xs text-gray-500">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Second Chart Cards Section */}
      <section className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2 w-full min-w-0">
        {/* Left Card: Bin Utilization Distribution - overflow-visible so tooltip is not clipped */}
        <div className="rounded-3xl border border-gray-200/60 bg-white p-3 md:p-4 shadow-sm relative overflow-visible">
          <h3 className="text-base md:text-lg font-semibold text-gray-900">Bin 
             Distribution</h3>

          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-6" style={{ marginTop: '50px' }}>
            {/* Donut Chart (same UI) */}
            <div className="relative flex items-center justify-center w-[200px] h-[200px] md:w-[250px] md:h-[250px] mx-auto lg:mx-0">
              <svg width="250" height="250" viewBox="0 0 280 280" className="overflow-visible" preserveAspectRatio="xMidYMid meet">
                {/* Helper function to convert angle to coordinates */}
                {(() => {
                  const centerX = 140;
                  const centerY = 140;
                  const outerRadius = 120;
                  const innerRadius = 80;

                  // Active mapping: use API-provided ACTIVE bin counts (do not rely on client-side bin lists).
                  // OLD (commented): derive counts from a bins[] list if present.
                  // const bins = (binUtilizationData && Array.isArray(binUtilizationData.bins)) ? binUtilizationData.bins : [];
                  // const totalBins = bins.length || (binUtilizationData?.totalBins ?? 0);
                  // const totalGoodBins = bins.length ? bins.filter((b) => b?.binType === "GOOD").length : (binUtilizationData?.totalGoodBins ?? 0);
                  // const totalHoldBins = bins.length ? bins.filter((b) => b?.binType === "HOLD").length : (binUtilizationData?.totalHoldBins ?? 0);

                  const totalBins = binUtilizationData?.totalBins ?? 0;
                  const totalGoodBins = binUtilizationData?.totalGoodBins ?? 0;
                  const totalHoldBins = binUtilizationData?.totalHoldBins ?? 0;

                  // OLD utilization buckets (commented) kept for rollback
                  // const overUtilized = binUtilizationData?.overUtilized ?? 0;
                  // const optimal = binUtilizationData?.optimal ?? 0;
                  // const underUtilized = binUtilizationData?.underUtilized ?? 0;
                  // const lowUtilization = binUtilizationData?.lowUtilization ?? 0;
                  // const total = overUtilized + optimal + underUtilized + lowUtilization || 1;

                  // Convert degrees to radians
                  const toRadians = (deg) => (deg * Math.PI) / 180;

                  // Get point on circle
                  const getPoint = (angle, radius) => {
                    const rad = toRadians(angle - 90); // Start from top
                    return {
                      x: centerX + radius * Math.cos(rad),
                      y: centerY + radius * Math.sin(rad)
                    };
                  };

                  const total = (totalGoodBins + totalHoldBins) || 1;
                  let currentAngle = 0;

                  const goodAngle = (totalGoodBins / total) * 360;
                  const goodStart = getPoint(currentAngle, outerRadius);
                  const goodEnd = getPoint(currentAngle + goodAngle, outerRadius);
                  const goodInnerStart = getPoint(currentAngle, innerRadius);
                  const goodInnerEnd = getPoint(currentAngle + goodAngle, innerRadius);
                  const goodLargeArc = goodAngle > 180 ? 1 : 0;

                  currentAngle += goodAngle;

                  const holdAngle = (totalHoldBins / total) * 360;
                  const holdStart = getPoint(currentAngle, outerRadius);
                  const holdEnd = getPoint(currentAngle + holdAngle, outerRadius);
                  const holdInnerStart = getPoint(currentAngle, innerRadius);
                  const holdInnerEnd = getPoint(currentAngle + holdAngle, innerRadius);
                  const holdLargeArc = holdAngle > 180 ? 1 : 0;

                  const midRadius = (outerRadius + innerRadius) / 2;
                  const segments = [
                    {
                      pathD: `M ${goodStart.x} ${goodStart.y} A ${outerRadius} ${outerRadius} 0 ${goodLargeArc} 1 ${goodEnd.x} ${goodEnd.y} L ${goodInnerEnd.x} ${goodInnerEnd.y} A ${innerRadius} ${innerRadius} 0 ${goodLargeArc} 0 ${goodInnerStart.x} ${goodInnerStart.y} Z`,
                      fill: '#6ee7b7',
                      startAngle: 0,
                      angle: goodAngle,
                      value: totalGoodBins,
                      label: 'GOOD Bins',
                    },
                    {
                      pathD: `M ${holdStart.x} ${holdStart.y} A ${outerRadius} ${outerRadius} 0 ${holdLargeArc} 1 ${holdEnd.x} ${holdEnd.y} L ${holdInnerEnd.x} ${holdInnerEnd.y} A ${innerRadius} ${innerRadius} 0 ${holdLargeArc} 0 ${holdInnerStart.x} ${holdInnerStart.y} Z`,
                      fill: '#fbbf24',
                      startAngle: currentAngle,
                      angle: holdAngle,
                      value: totalHoldBins,
                      label: 'HOLD Bins',
                    },
                  ];

                  return (
                    <>
                      {segments.map((s, i) => {
                        const mid = getPoint(s.startAngle + s.angle / 2, midRadius);
                        return (
                          <path
                            key={i}
                            d={s.pathD}
                            fill={s.fill}
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredBinSegment({ x: mid.x, y: mid.y, value: s.value, label: s.label })}
                            onMouseLeave={() => setHoveredBinSegment(null)}
                          />
                        );
                      })}

                      {/* Center white circle with dotted ring */}
                      <circle cx="140" cy="140" r="80" fill="white" />
                      <circle
                        cx="140"
                        cy="140"
                        r="80"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="2"
                        strokeDasharray="4 4"
                      />

                      {/* Center text (same UI) */}
                      <text x="140" y="135" textAnchor="middle" fontSize="36" fill="#111827" fontWeight="600">{totalBins}</text>
                      <text x="140" y="155" textAnchor="middle" fontSize="14" fill="#6b7280">Total Bins</text>

                      {/* Tooltip last so it renders above chart segments and center */}
                      {hoveredBinSegment !== null && (
                        <g transform={`translate(${hoveredBinSegment.x}, ${hoveredBinSegment.y})`} pointerEvents="none">
                          <rect x="-55" y="-32" width="110" height="40" rx="8" fill="white" stroke="#e5e7eb" strokeWidth="1.5" filter="drop-shadow(0 2px 8px rgba(0,0,0,0.15))" />
                          <text x="0" y="-12" fill="#111827" fontSize="14" textAnchor="middle" fontWeight="600">{hoveredBinSegment.value}</text>
                          <text x="0" y="4" fill="#6b7280" fontSize="11" textAnchor="middle">{hoveredBinSegment.label}</text>
                        </g>
                      )}
                    </>
                  );
                })()}
              </svg>
            </div>

            {/* Legend (same layout; labels updated) */}
            <div className="space-y-4 w-full lg:flex-1 min-w-0 mt-16">
              {(() => {
                // Active mapping: use API-provided ACTIVE bin counts (do not rely on client-side bin lists).
                // OLD (commented): derive counts from a bins[] list if present.
                // const bins = (binUtilizationData && Array.isArray(binUtilizationData.bins)) ? binUtilizationData.bins : [];
                // const totalGoodBins = bins.length ? bins.filter((b) => b?.binType === "GOOD").length : (binUtilizationData?.totalGoodBins ?? 0);
                // const totalHoldBins = bins.length ? bins.filter((b) => b?.binType === "HOLD").length : (binUtilizationData?.totalHoldBins ?? 0);

                const totalGoodBins = binUtilizationData?.totalGoodBins ?? 0;
                const totalHoldBins = binUtilizationData?.totalHoldBins ?? 0;

                return (
                  <>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-4 w-4 rounded-md bg-emerald-300 flex-shrink-0"></div>
                      <span className="text-xs font-semibold text-gray-900 w-6 flex-shrink-0">{totalGoodBins}</span>
                      <span className="text-xs text-gray-500 truncate">GOOD Bins</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-4 w-4 rounded-md bg-yellow-400 flex-shrink-0"></div>
                      <span className="text-xs font-semibold text-gray-900 w-6 flex-shrink-0">{totalHoldBins}</span>
                      <span className="text-xs text-gray-500 truncate">HOLD Bins</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right Card: Returns Trend (Last 7 Days) */}
        <div className="rounded-3xl border border-gray-200/60 bg-white p-3 md:p-4 shadow-sm overflow-x-auto">
          <h3 className="text-base font-semibold text-gray-900">Returns Trend (Last 7 Days)</h3>

          {/* Area Chart */}
          <div className="mt-6 h-[250px] md:h-[300px] relative overflow-x-auto">
            {(() => {
              const days = returnsTrendData?.days ?? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              const values = returnsTrendData?.values ?? [0, 0, 0, 0, 0, 0, 0];
              const maxVal = Math.max(1, ...values);
              const xPos = days.map((_, i) => 100 + (i / Math.max(1, days.length - 1)) * 450);
              const toY = (v) => 320 - (v / maxVal) * 270;
              
              const linePath = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xPos[i]} ${toY(v)}`).join(' ');
              const lastX = xPos[days.length - 1] ?? 550;
              const fillPath = linePath + ` L ${lastX} 330 L 100 330 Z`;
              
              const maxIdx = values.reduce((iMax, v, i, arr) => v >= arr[iMax] ? i : iMax, 0);
              
              return (
                <svg width="100%" height="100%" viewBox="0 0 600 340" preserveAspectRatio="xMidYMid meet" className="overflow-visible">
                  <defs>
                    <linearGradient id="blueFill" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.05" />
                    </linearGradient>
                  </defs>
                  <line x1="50" y1="50" x2="550" y2="50" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="50" y1="120" x2="550" y2="120" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="50" y1="190" x2="550" y2="190" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="50" y1="260" x2="550" y2="260" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="50" y1="320" x2="550" y2="320" stroke="#e5e7eb" strokeWidth="1" />
                  <path d={fillPath} fill="url(#blueFill)" />
                  <path d={linePath} fill="none" stroke="#0ea5e9" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  
                  {maxIdx >= 0 && values[maxIdx] > 0 && (
                    <rect x={xPos[maxIdx] - 40} y="50" width="80" height="270" fill="#dbeafe" opacity="0.5" />
                  )}
                  
                  {values.map((v, i) => (
                    <g key={`dot-${i}`}>
                      <circle 
                        cx={xPos[i]} 
                        cy={toY(v)} 
                        r="15" 
                        fill="transparent"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setHoveredReturnDay(i)}
                        onMouseLeave={() => setHoveredReturnDay(null)}
                      />
                      <circle 
                        cx={xPos[i]} 
                        cy={toY(v)} 
                        r="6" 
                        fill="#0ea5e9" 
                        stroke="white" 
                        strokeWidth="2"
                        pointerEvents="none"
                      />
                    </g>
                  ))}
                  
                  {hoveredReturnDay !== null && values[hoveredReturnDay] >= 0 && (
                    <g transform={`translate(${xPos[hoveredReturnDay]}, ${Math.max(60, toY(values[hoveredReturnDay]) - 45)})`} pointerEvents="none">
                      <rect x="-40" y="-28" width="80" height="36" rx="8" fill="white" stroke="#e5e7eb" strokeWidth="1.5" filter="drop-shadow(0 2px 8px rgba(0,0,0,0.15))" />
                      <text x="0" y="-10" fill="#111827" fontSize="14" textAnchor="middle" fontWeight="600">{values[hoveredReturnDay]}</text>
                      <text x="0" y="6" fill="#6b7280" fontSize="11" textAnchor="middle">Returns</text>
                    </g>
                  )}
                  
                  {days.map((d, i) => (
                    i === maxIdx && values[i] > 0 ? (
                      <g key={i}>
                        <rect x={xPos[i] - 22} y="315" width="44" height="22" rx="11" fill="#38bdf8" />
                        <text x={xPos[i]} y="330" fill="white" fontSize="11" textAnchor="middle" fontWeight="500">{d}</text>
                      </g>
                    ) : (
                      <text key={i} x={xPos[i]} y="335" fill="#6b7280" fontSize="12" textAnchor="middle">{d}</text>
                    )
                  ))}
                </svg>
              );
            })()}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DashboardDemo;
